/**
 * Default: Components / Shared > Form > Range
 *
 * @copyright 2026 i-fabrik GmbH
 * @author Tobias Garz
 */

// Must match $range-thumb-size in _index.scss
const THUMB_WIDTH = 22;

export class RangeSlider {
	/**
	 * @param {HTMLInputElement} rangeEl
	 * @param {object} [options]
	 * @param {number} [options.min]
	 * @param {number} [options.max]
	 * @param {number} [options.step]
	 * @param {number} [options.value]
	 * @param {Array<{value: number, label?: string|number, output?: number}>} [options.map]
	 * @param {function} [options.onRender] - ({ sliderValue, entry, bubbleLabel, rangeEl, iconEl }) => void
	 */
	constructor(rangeEl, options = {}) {
		this.range   = rangeEl;
		this.options = options;
		this.map     = options.map ?? null;

		// Apply attribute overrides from options
		if (options.min   !== undefined) rangeEl.min   = options.min;
		if (options.max   !== undefined) rangeEl.max   = options.max;
		if (options.step  !== undefined) rangeEl.step  = options.step;
		if (options.value !== undefined) rangeEl.value = options.value;

		// Derive min/max/step from map when HTML attributes are absent
		if (this.map?.length) {
			if (rangeEl.min  === '') rangeEl.min  = this.map[0].value;
			if (rangeEl.max  === '') rangeEl.max  = this.map.at(-1).value;
			if (rangeEl.step === '') rangeEl.step = 1;
		}

		this.min = Number(rangeEl.min)  || 0;
		this.max = Number(rangeEl.max)  || 100;

		const targetSel = rangeEl.dataset.target;
		this.field  = targetSel ? document.querySelector(targetSel) : null;

		const wrapper  = rangeEl.closest('.range-field');
		this.bubble    = wrapper?.querySelector('[data-range-bubble]') ?? null;
		this.iconEl    = wrapper?.querySelector('[data-range-icon] i') ?? null;
	}

	init() {
		this.range.dataset.rangeInit = '1';

		const sliderValue = this._initialValue();
		this.range.value = sliderValue;
		this._render(sliderValue);

		this.range.addEventListener('input', () => this._onSliderInput());
		if (this.field) {
			this.field.addEventListener('change', () => this._onFieldChange());
		}

		return this;
	}

	_initialValue() {
		if (this.field?.value) {
			const num = Number(this.field.value);
			if (!isNaN(num)) {
				return this.map ? this._outputToValue(num) : this._clamp(num);
			}
		}
		return Number(this.range.value) || this.min;
	}

	_onSliderInput() {
		const sliderValue = Number(this.range.value);

		if (this.field) {
			const output = this.map
				? (this._findEntry(sliderValue)?.output ?? sliderValue)
				: sliderValue;
			this.field.value = output;
		}

		this._render(sliderValue);
	}

	_onFieldChange() {
		const rawVal = this.field.value;
		if (!rawVal) return;
		const num = Number(rawVal);
		if (isNaN(num)) return;

		const sliderValue = this.map
			? this._outputToValue(num)
			: this._clamp(num);

		this.range.value = sliderValue;
		// Field value intentionally not overwritten
		this._render(sliderValue);
	}

	_render(sliderValue) {
		const { min, max } = this;
		const pct    = (sliderValue - min) / (max - min);
		const offset = (0.5 - pct) * THUMB_WIDTH;
		const left   = `calc(${pct * 100}% + ${offset}px)`;

		this.range.style.setProperty('--fill', `${pct * 100}%`);

		const entry       = this.map ? this._findEntry(sliderValue) : null;
		const bubbleLabel = entry?.label !== undefined ? entry.label : sliderValue;

		if (this.bubble) {
			this.bubble.innerHTML = bubbleLabel;
			this.bubble.style.left  = left;
		}

		if (this.iconEl) {
			this.iconEl.closest('[data-range-icon]').style.left = left;
		}

		this.range.setAttribute('aria-valuetext', String(bubbleLabel));

		if (typeof this.options.onRender === 'function') {
			this.options.onRender({
				sliderValue,
				entry,
				bubbleLabel,
				rangeEl : this.range,
				iconEl  : this.iconEl,
			});
		}
	}

	_findEntry(sliderValue) {
		return this.map.find(e => e.value === sliderValue) ?? null;
	}

	_outputToValue(output) {
		let best     = this.map[0];
		let bestDist = Math.abs((best.output ?? best.value) - output);

		for (const entry of this.map) {
			const dist = Math.abs((entry.output ?? entry.value) - output);
			if (dist < bestDist || (dist === bestDist && entry.value < best.value)) {
				best     = entry;
				bestDist = dist;
			}
		}

		return best.value;
	}

	_clamp(value) {
		return Math.min(Math.max(value, this.min), this.max);
	}
}

/**
 * Auto-initialize all .form-range elements that have not been manually initialized.
 * Works linearly based on HTML min/max/step attributes.
 */
export function initRanges() {
	document.querySelectorAll('.form-range').forEach((el) => {
		if (el.dataset.rangeInit) return;
		new RangeSlider(el).init();
	});
}
